import React, { useState, useEffect } from 'react';
import { X, MessageCircle, Minimize2, Maximize2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ChatBubbleProps {
  senderName: string;
  message: string;
  senderImageUrl?: string;
  conversationId: string;
  onClose: () => void;
  onOpenChatWidget?: (conversationId: string) => void;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  senderName,
  message,
  senderImageUrl,
  conversationId,
  onClose,
  onOpenChatWidget
}) => {
  const navigate = useNavigate();
  const [isMinimized, setIsMinimized] = useState(false);

  const handleOpenChat = () => {
    if (onOpenChatWidget) {
      onOpenChatWidget(conversationId);
    } else {
      navigate('/social');
    }
    onClose();
  };

  // Auto-close after 10 seconds if not interacted with
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isMinimized) {
        onClose();
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [isMinimized, onClose]);

  if (isMinimized) {
    return (
      <div
        className="fixed bottom-4 right-4 z-50 flex items-center space-x-2 bg-accent-blue text-white px-4 py-2 rounded-full shadow-lg cursor-pointer hover:bg-accent-blue transition-colors"
        onClick={() => setIsMinimized(false)}
      >
        {senderImageUrl ? (
          <img
            src={senderImageUrl}
            alt={senderName}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
            <MessageCircle className="w-4 h-4" />
          </div>
        )}
        <span className="font-medium">{senderName}</span>
        <Maximize2 className="w-4 h-4" />
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-dark-card border border-dark-border rounded-lg shadow-2xl overflow-hidden animate-slide-up">
      {/* Header */}
      <div className="bg-accent-blue text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          {senderImageUrl ? (
            <img
              src={senderImageUrl}
              alt={senderName}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
              <MessageCircle className="w-4 h-4" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{senderName}</p>
            <p className="text-xs opacity-90">Messenger</p>
          </div>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Message Content */}
      <div className="p-4 bg-dark-bg">
        <div className="mb-4">
          <p className="text-text-secondary text-sm line-clamp-3">{message}</p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex space-x-2">
          <button
            onClick={handleOpenChat}
            className="flex-1 bg-accent-blue text-white px-4 py-2 rounded-md hover:bg-accent-blue transition-colors text-sm font-medium"
          >
            Open Chat
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-dark-card text-text-secondary rounded-md hover:bg-dark-bg transition-colors text-sm border border-dark-border"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatBubble;

